import { useState, useCallback, useEffect, useMemo } from 'react';
import { Text, Button, Flex, Spinner } from '@radix-ui/themes';
import { Dialog } from '@/components/shared/dialog';
import Select, { type SelectOption } from '@/components/shared/select';
import { toast } from 'sonner';
import styles from './VerifySnippetDialog.module.scss';

const ROOT_OPTION = 'Root';

const toOptionValue = (path: string) => (path ? path : ROOT_OPTION);
const toCollectionPath = (value: string) => (value === ROOT_OPTION ? '' : value);

interface CreateCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultParentPath: string;
  collectionPaths: string[];
  onCreateCollection: (parentCollectionPath: string, collectionName: string, snippetName: string) => Promise<void>;
}

export function CreateCollectionDialog({
  open,
  onOpenChange,
  defaultParentPath,
  collectionPaths,
  onCreateCollection,
}: CreateCollectionDialogProps) {
  const [collectionName, setCollectionName] = useState('');
  const [snippetName, setSnippetName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedParentPath, setSelectedParentPath] = useState('');

  const options = useMemo<SelectOption[]>(() => {
    const unique = new Set(collectionPaths.length > 0 ? collectionPaths : ['']);
    if (!unique.has('')) {
      unique.add('');
    }
    return Array.from(unique).map((path) => {
      const value = toOptionValue(path);
      return { label: value, value };
    });
  }, [collectionPaths]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const normalizedDefault = defaultParentPath.startsWith('/')
      ? defaultParentPath.slice(1)
      : defaultParentPath;
    const available = options.map((option) => toCollectionPath(option.value));
    const nextPath = available.includes(normalizedDefault)
      ? normalizedDefault
      : available[0] ?? '';
    setSelectedParentPath(nextPath);
  }, [open, defaultParentPath, options]);

  const handleCreate = useCallback(async () => {
    if (collectionName.trim() && snippetName.trim() && !isLoading) {
      setIsLoading(true);
      try {
        await onCreateCollection(selectedParentPath, collectionName.trim(), snippetName.trim());
        toast.success('Collection created successfully');
        setCollectionName('');
        setSnippetName('');
        onOpenChange(false);
      } catch (error) {
        toast.error('Failed to create collection. Please try again.');
        console.error('Error creating collection:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [collectionName, snippetName, isLoading, onCreateCollection, onOpenChange]);

  const handleCancel = useCallback(() => {
    if (!isLoading) {
      setCollectionName('');
      setSnippetName('');
      setSelectedParentPath('');
      onOpenChange(false);
    }
  }, [isLoading, onOpenChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && collectionName.trim() && snippetName.trim() && !isLoading) {
        handleCreate();
      }
    },
    [collectionName, snippetName, isLoading, handleCreate]
  );

  return (
    <Dialog open={open} onOpenChange={(open) => !isLoading && onOpenChange(open)}>
      <Dialog.Content size="medium" className={styles.dialogContent}>
        <Dialog.Close className={styles.closeButton} disabled={isLoading} />
        <Flex direction="column" gap="6" p="5">
          <Flex direction="column" gap="1">
            <Text size="4" weight="medium" className={styles.title}>
              Create Collection
            </Text>
            <Text size="3" color="gray" className={styles.subtitle}>
              Choose a parent collection, then provide collection and placeholder snippet names.
            </Text>
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Parent collection</Text>
            </label>
            <Select
              options={options}
              value={toOptionValue(selectedParentPath)}
              onChange={(value) => setSelectedParentPath(toCollectionPath(value))}
              allowSearch
              disabled={isLoading}
            />
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Collection name</Text>
            </label>
            <input
              type="text"
              className={styles.input}
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter collection name"
              disabled={isLoading}
              autoFocus
            />
          </Flex>

          <Flex direction="column" gap="2">
            <label className={styles.label}>
              <Text size="2">Snippet name</Text>
            </label>
            <input
              type="text"
              className={styles.input}
              value={snippetName}
              onChange={(e) => setSnippetName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter snippet name"
              disabled={isLoading}
            />
          </Flex>

          <Flex gap="3" justify="end">
            <Button
              variant="ghost"
              size="2"
              onClick={handleCancel}
              className={styles.cancelButton}
              disabled={isLoading}
            >
              <Text size="3" color="gray">
                Cancel
              </Text>
            </Button>
            <Button
              variant="solid"
              color="gray"
              size="2"
              highContrast
              onClick={handleCreate}
              disabled={!collectionName.trim() || !snippetName.trim() || isLoading}
              className={styles.verifyButton}
            >
              {isLoading ? (
                <>
                  <Spinner size="1" />
                  <Text size="3">Creating...</Text>
                </>
              ) : (
                <Text size="3">Create</Text>
              )}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
